/**
 * Slate Icons - Shared Icon Components
 *
 * ⚠️ These are SIMPLIFIED RECREATIONS of Slate icons for prototyping only.
 * They follow the Slate icon design patterns:
 * - 24x24 viewBox (default)
 * - 1.5 stroke width
 * - Round line caps and joins
 * - currentColor for easy theming
 *
 * For real Slate icons, see: @faire/slate/components/icons
 */
import React from "react";

interface IconProps {
  /** Icon size in pixels */
  size?: number;
  /** Additional CSS class names */
  className?: string;
  /** Custom color (defaults to currentColor) */
  color?: string;
}

/**
 * Chevron Left Icon (FS Chevron Left)
 * Used for: Back navigation
 */
export function ChevronLeftIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M15 18L9 12L15 6"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Chevron Right Icon (FS Chevron Right)
 * Used for: Forward navigation
 */
export function ChevronRightIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M9 6L15 12L9 18"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Chevron Down Icon (FS Chevron Down)
 * Used for: Dropdowns, expandable sections
 */
export function ChevronDownIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M6 9L12 15L18 9"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Chevron Up Icon (FS Chevron Up)
 * Used for: Collapse sections
 */
export function ChevronUpIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M18 15L12 9L6 15"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Close Icon (FS Close / X Icon)
 * Used for: Modal close, dismiss, remove
 */
export function CloseIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M18 6L6 18M6 6L18 18"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Lock Icon (FS Lock)
 * Used for: Security, privacy indicators
 */
export function LockIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect
        x="5"
        y="11"
        width="14"
        height="10"
        rx="2"
        stroke={color}
        strokeWidth="1.5"
      />
      <path
        d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Check Icon (FS Check / Checkmark)
 * Used for: Confirmation, success, selection
 */
export function CheckIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M20 6L9 17L4 12"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Check Circle Solid Icon (FS Check + Outline Solid)
 * Used for: Autosaved states, completed status
 */
export function CheckCircleSolidIcon({
  size = 16,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx="8" cy="8" r="8" fill={color} />
      <path
        d="M4.5 8L7 10.5L11.5 5.5"
        stroke="white"
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Search Icon (FS Search)
 * Used for: Search fields
 */
export function SearchIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx="11" cy="11" r="7" stroke={color} strokeWidth="1.5" />
      <path
        d="M21 21L16 16"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Info Icon (FS Info)
 * Used for: Information, help
 */
export function InfoIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" />
      <path
        d="M12 16V12"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="12" cy="8" r="1" fill={color} />
    </svg>
  );
}

/**
 * Warning Icon (FS Warning / Alert)
 * Used for: Warnings, alerts
 */
export function WarningIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 9V13"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="12" cy="17" r="1" fill={color} />
      <path
        d="M10.29 3.86L1.82 18C1.64 18.3 1.55 18.6 1.55 18.9C1.55 19.2 1.64 19.5 1.82 19.74C2 19.99 2.25 20.18 2.54 20.3C2.83 20.42 3.15 20.45 3.46 20.4H20.54C20.85 20.45 21.17 20.42 21.46 20.3C21.75 20.18 22 19.99 22.18 19.74C22.36 19.5 22.45 19.2 22.45 18.9C22.45 18.6 22.36 18.3 22.18 18L13.71 3.86C13.53 3.56 13.27 3.32 12.97 3.16C12.66 3.01 12.33 2.94 12 2.94C11.67 2.94 11.34 3.01 11.03 3.16C10.73 3.32 10.47 3.56 10.29 3.86Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Calendar Icon (FS Calendar)
 * Path aligned with @faire/slate-icons/Calendar
 * Used for: Date selection, scheduling, monthly billing
 */
export function CalendarIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M2.571 4.285A1.714 1.714 0 0 0 .857 6v15.428a1.714 1.714 0 0 0 1.714 1.714h18.857a1.714 1.714 0 0 0 1.715-1.714V6a1.714 1.714 0 0 0-1.715-1.715H18M.857 11.143h22.286M6 .858v6.857M18 .858v6.857M6 4.285h8.571"
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Payment Plan Icon (FS Payment Plan)
 * Paths aligned with @faire/slate-icons/PaymentPlan — sync / payment-schedule metaphor.
 */
export function PaymentPlanIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <path
        d="M12 7.714V5.143m0 2.571c-1.42 0-2.571 0-2.571 1.715 0 2.571 5.143 2.571 5.143 5.143 0 1.714-1.153 1.714-2.572 1.714m0-8.572c1.42 0 2.572.65 2.572 1.715M12 16.286v2.571m0-2.571c-1.42 0-2.571-.429-2.571-1.715m9.428.858 3.429-.858m0 0L23.143 18m-.857-3.429A11.31 11.31 0 0 1 12 22.286a10.32 10.32 0 0 1-9.663-6.772m2.806-6.943-3.429.858m0 0L.857 6m.857 3.429A11.63 11.63 0 0 1 12 1.714a10.32 10.32 0 0 1 9.664 6.772"
        stroke={color}
        strokeWidth="1"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Shield Check Icon (for security/verification)
 * Used for: Socure/verification context
 */
export function ShieldCheckIcon({
  size = 24,
  className = "",
  color = "currentColor",
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 2L4 6V12C4 16.42 7.42 20.28 12 22C16.58 20.28 20 16.42 20 12V6L12 2Z"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 12L11 14L15 10"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Faire Logo (Brand Mark)
 * The official Faire wordmark SVG
 * Source: https://www.figma.com/design/qcB5qVN7uKBBYHN49ihDE7/Slate-Icons?node-id=1956-1098
 */
export function FaireLogo({
  width = 98,
  height = 24,
  className = "",
  color = "#333333",
}: {
  width?: number;
  height?: number;
  className?: string;
  color?: string;
}) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 98 24"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M92.9999 14.1378L92.2477 17.0002H83.041V16.8503C83.2917 16.8103 83.5249 16.7454 83.7405 16.6555C83.9562 16.5655 84.1342 16.4407 84.2746 16.2808C84.3247 16.2209 84.3649 16.1334 84.3949 16.0185C84.425 15.9036 84.4526 15.7713 84.4777 15.6214C84.5028 15.4715 84.5203 15.3142 84.5303 15.1493C84.5404 14.9845 84.5454 14.8221 84.5454 14.6623C84.5454 14.5224 84.5479 14.3326 84.5529 14.0928C84.5579 13.853 84.5629 13.5908 84.5679 13.306C84.573 13.0213 84.5755 12.729 84.5755 12.4293C84.5755 12.1196 84.5755 11.8299 84.5755 11.5601V8.63775C84.5755 8.45792 84.573 8.27309 84.5679 8.08326C84.5629 7.89343 84.5529 7.71359 84.5379 7.54375C84.5228 7.3739 84.4977 7.22154 84.4626 7.08666C84.4275 6.95179 84.3799 6.85438 84.3197 6.79443C84.1693 6.63457 84.0088 6.53217 83.8383 6.48721C83.6678 6.44225 83.4472 6.39979 83.1764 6.35982V6.20996H91.4805L92.5034 8.68271L92.353 8.75764C91.5406 7.74856 90.7784 7.06918 90.0664 6.7195C89.8959 6.63957 89.58 6.56963 89.1186 6.50969C88.6573 6.44974 88.0957 6.41977 87.4337 6.41977C87.2231 6.41977 87.005 6.42726 86.7793 6.44225C86.5537 6.45724 86.3907 6.47472 86.2904 6.4947C86.2704 6.65456 86.2528 6.98425 86.2378 7.4838C86.2227 7.98335 86.2152 8.56282 86.2152 9.22222C86.2152 9.60188 86.2152 9.92159 86.2152 10.1814C86.2152 10.4411 86.2177 10.6534 86.2227 10.8183C86.2277 10.9831 86.2303 11.1105 86.2303 11.2004C86.2303 11.2804 86.2303 11.3353 86.2303 11.3653C86.2804 11.3653 86.3807 11.3653 86.5311 11.3653C86.6816 11.3653 86.8546 11.3603 87.0501 11.3503C87.2457 11.3403 87.4413 11.3278 87.6368 11.3128C87.8324 11.2978 87.9954 11.2754 88.1257 11.2454C88.4266 11.1754 88.6773 11.0805 88.8779 10.9606C89.0785 10.8408 89.2415 10.6959 89.3668 10.526C89.4922 10.3562 89.5925 10.1589 89.6677 9.93408C89.7429 9.70928 89.8106 9.45701 89.8708 9.17726H90.0513V13.898H89.8708C89.7906 13.5783 89.6677 13.2486 89.5022 12.9089C89.3368 12.5692 89.1638 12.3144 88.9832 12.1446C88.8228 12.0047 88.6297 11.8898 88.4041 11.7999C88.1784 11.71 87.9151 11.655 87.6143 11.635C87.4638 11.625 87.3385 11.6175 87.2382 11.6125C87.1379 11.6076 87.0376 11.6051 86.9373 11.6051C86.847 11.6051 86.7468 11.6051 86.6364 11.6051C86.5361 11.6051 86.4058 11.6051 86.2453 11.6051C86.2252 11.7449 86.2152 11.9497 86.2152 12.2195C86.2152 12.4893 86.2152 12.769 86.2152 13.0587C86.2152 13.1886 86.2152 13.3385 86.2152 13.5083C86.2152 13.6782 86.2152 13.848 86.2152 14.0179C86.2152 14.1777 86.2177 14.3301 86.2227 14.4749C86.2277 14.6198 86.2303 14.7422 86.2303 14.8421C86.2403 15.1618 86.2578 15.464 86.2829 15.7488C86.308 16.0335 86.3656 16.2458 86.4559 16.3857C86.5462 16.5256 86.7618 16.6255 87.1028 16.6854C87.4438 16.7454 87.8148 16.7754 88.216 16.7754C88.3865 16.7754 88.6046 16.7704 88.8704 16.7604C89.1362 16.7504 89.4145 16.7254 89.7053 16.6854C90.0263 16.6455 90.3221 16.568 90.5929 16.4531C90.8637 16.3383 91.1244 16.1759 91.3752 15.9661C91.6259 15.7563 91.8691 15.499 92.1048 15.1943C92.3405 14.8896 92.5837 14.5224 92.8344 14.0928L92.9999 14.1378ZM72.9218 17.0005H69.8378C69.7476 16.9106 69.6824 16.8431 69.6423 16.7982C69.6022 16.7532 69.557 16.7008 69.5069 16.6408C69.4567 16.5809 69.3941 16.4935 69.3188 16.3786C69.2436 16.2637 69.1258 16.0913 68.9653 15.8615C68.7748 15.5818 68.6068 15.3295 68.4614 15.1047C68.3159 14.8799 68.168 14.6576 68.0176 14.4378C67.8671 14.218 67.7042 13.9957 67.5287 13.7709C67.3531 13.5461 67.145 13.2939 66.9043 13.0141C66.7038 12.7843 66.4856 12.5695 66.25 12.3697C66.0143 12.1699 65.766 12.045 65.5053 11.995C65.3147 11.9551 65.1693 11.9351 65.069 11.9351C64.9788 11.9351 64.8634 11.9301 64.723 11.9201V13.6136C64.723 14.0931 64.728 14.4978 64.7381 14.8275C64.7481 15.1472 64.7581 15.4219 64.7682 15.6517C64.7782 15.8815 64.8333 16.0713 64.9336 16.2212C65.0139 16.3511 65.1693 16.4835 65.4 16.6183C65.6307 16.7532 65.9516 16.8306 66.3628 16.8506V17.0005H61.5488V16.8506C61.7996 16.8107 62.0403 16.7507 62.2709 16.6708C62.5016 16.5909 62.6821 16.471 62.8125 16.3111C62.8626 16.2512 62.9028 16.1613 62.9328 16.0414C62.9629 15.9215 62.9855 15.7866 63.0005 15.6367C63.0156 15.4869 63.0281 15.327 63.0381 15.1572C63.0482 14.9873 63.0532 14.8225 63.0532 14.6626C63.0532 14.5227 63.0557 14.3329 63.0607 14.0931C63.0657 13.8533 63.0707 13.5911 63.0758 13.3063C63.0808 13.0216 63.0833 12.7294 63.0833 12.4296V8.63809C63.0833 8.45825 63.0808 8.27592 63.0758 8.09109C63.0707 7.90626 63.0607 7.73142 63.0457 7.56657C63.0306 7.40171 63.0055 7.25185 62.9704 7.11697C62.9353 6.9821 62.8877 6.88469 62.8275 6.82474C62.6771 6.66488 62.5091 6.55748 62.3236 6.50253C62.138 6.44758 61.9099 6.40013 61.6391 6.36016V6.2103H62.2559C62.5969 6.2103 62.983 6.2078 63.4142 6.20281C63.8455 6.19781 64.2767 6.19531 64.708 6.19531H65.761C66.433 6.19531 67.0297 6.21529 67.5512 6.25526C68.0727 6.29522 68.5491 6.4301 68.9804 6.65989C69.4216 6.88968 69.7777 7.2019 70.0485 7.59654C70.3192 7.99118 70.4546 8.48822 70.4546 9.08768C70.4546 9.45735 70.3869 9.78954 70.2515 10.0843C70.1162 10.379 69.9356 10.6388 69.71 10.8636C69.4843 11.0884 69.2211 11.2807 68.9202 11.4405C68.6193 11.6004 68.3034 11.7353 67.9724 11.8452V11.8901C68.1429 11.9801 68.3084 12.1099 68.4689 12.2798C68.6293 12.4496 68.7848 12.6395 68.9352 12.8493C69.196 13.2289 69.4793 13.6385 69.7852 14.0781C70.0911 14.5177 70.3644 14.9074 70.6051 15.2471C70.9761 15.7666 71.3372 16.1463 71.6882 16.386C72.0392 16.6258 72.4504 16.7807 72.9218 16.8506V17.0005ZM68.7247 8.99758C68.7247 8.49803 68.5994 8.05344 68.3487 7.66379C68.0979 7.27414 67.7319 6.96443 67.2505 6.73464C67.0098 6.62474 66.7565 6.54481 66.4908 6.49485C66.225 6.4449 65.9467 6.41992 65.6558 6.41992C65.4452 6.41992 65.2747 6.42492 65.1444 6.43491C65.014 6.4449 64.9137 6.45489 64.8435 6.46488C64.8335 6.52483 64.8234 6.57728 64.8134 6.62224C64.8034 6.6672 64.7959 6.72464 64.7908 6.79458C64.7858 6.86452 64.7808 6.96193 64.7758 7.08682C64.7708 7.2117 64.7633 7.38904 64.7532 7.61883C64.7432 7.96852 64.7357 8.36315 64.7307 8.80276C64.7257 9.24236 64.7231 9.66697 64.7231 10.0766V11.7251C65.1343 11.7251 65.5179 11.7076 65.874 11.6726C66.23 11.6377 66.5735 11.5652 66.9045 11.4553C67.2254 11.3554 67.5012 11.2206 67.7319 11.0507C67.9625 10.8809 68.1506 10.6885 68.296 10.4737C68.4414 10.2589 68.5492 10.0266 68.6194 9.77687C68.6896 9.5271 68.7247 9.26733 68.7247 8.99758ZM50.151 17.0002H45.5928V16.8503C45.8435 16.8103 46.0767 16.7454 46.2923 16.6555C46.5079 16.5655 46.691 16.4357 46.8414 16.2658C46.8915 16.2059 46.9291 16.121 46.9542 16.0111C46.9793 15.9012 46.9993 15.7713 47.0144 15.6214C47.0294 15.4715 47.0395 15.3142 47.0445 15.1493C47.0495 14.9845 47.052 14.8221 47.052 14.6623C47.052 14.5224 47.0545 14.3326 47.0595 14.0928C47.0645 13.853 47.0696 13.5908 47.0746 13.306C47.0796 13.0213 47.0821 12.729 47.0821 12.4293V8.09824C47.0821 7.91841 47.0746 7.75106 47.0595 7.5962C47.0445 7.44134 47.0219 7.30147 46.9918 7.17658C46.9617 7.0517 46.9166 6.95428 46.8564 6.88435C46.716 6.7145 46.543 6.59211 46.3374 6.51718C46.1318 6.44225 45.8987 6.3898 45.6379 6.35982V6.20996H50.1359V6.35982C49.9253 6.3898 49.6997 6.44475 49.459 6.52467C49.2183 6.6046 49.0327 6.76446 48.9024 7.00424C48.8121 7.17409 48.7645 7.38889 48.7594 7.64865C48.7544 7.90842 48.7469 8.15319 48.7369 8.38299C48.7369 8.63276 48.7344 8.88003 48.7294 9.12481C48.7243 9.36959 48.7218 9.61686 48.7218 9.86664V14.8871C48.7218 15.1768 48.7269 15.4566 48.7369 15.7263C48.7469 15.9961 48.792 16.1859 48.8723 16.2958C48.9926 16.4656 49.1832 16.5955 49.4439 16.6854C49.7047 16.7754 49.9404 16.8303 50.151 16.8503V17.0002ZM28.9895 7.91797C28.9294 8.04785 28.8416 8.25266 28.7263 8.53241C28.6109 8.81216 28.468 9.15185 28.2975 9.55148C28.127 9.95112 27.939 10.4032 27.7334 10.9078C27.5278 11.4123 27.3047 11.9593 27.064 12.5488H30.8098C30.6393 12.0992 30.4663 11.6496 30.2908 11.2C30.1153 10.7504 29.9448 10.3183 29.7793 9.90366C29.6139 9.48904 29.4634 9.10939 29.328 8.7647C29.1926 8.42001 29.0798 8.13777 28.9895 7.91797ZM30.9601 17V16.8501C30.9902 16.8501 31.0629 16.8401 31.1782 16.8202C31.2935 16.8002 31.4139 16.7652 31.5392 16.7153C31.6646 16.6653 31.7749 16.5954 31.8702 16.5054C31.9655 16.4155 32.0131 16.3006 32.0131 16.1608C32.0131 16.0708 31.9855 15.9235 31.9304 15.7187C31.8752 15.5139 31.7975 15.2641 31.6972 14.9693C31.5969 14.6746 31.4816 14.3474 31.3512 13.9877C31.2208 13.6281 31.0804 13.2534 30.93 12.8638H26.9284C26.7779 13.2434 26.6325 13.6056 26.4921 13.9503C26.3517 14.295 26.2113 14.6421 26.0709 14.9918C25.8603 15.5313 25.755 15.911 25.755 16.1308C25.755 16.2607 25.7951 16.3681 25.8753 16.453C25.9556 16.5379 26.0584 16.6079 26.1837 16.6628C26.3091 16.7178 26.4445 16.7577 26.5899 16.7827C26.7353 16.8077 26.8732 16.8302 27.0036 16.8501V17H23.7993V16.8351C24.0601 16.8052 24.2832 16.7352 24.4688 16.6253C24.6543 16.5154 24.7621 16.4405 24.7922 16.4005C24.8624 16.3306 24.9777 16.1658 25.1382 15.906C25.2987 15.6462 25.4942 15.2616 25.7249 14.752C25.9355 14.2825 26.1712 13.748 26.4319 13.1485C26.6927 12.549 26.961 11.9321 27.2368 11.2977C27.5126 10.6633 27.7859 10.0238 28.0567 9.37943C28.3274 8.73501 28.5832 8.13306 28.8239 7.57357L28.6734 7.24387C28.854 7.16394 29.0169 7.06903 29.1624 6.95913C29.3078 6.84923 29.4356 6.73433 29.546 6.61444C29.6563 6.49455 29.7491 6.37966 29.8243 6.26975C29.8995 6.15985 29.9572 6.06994 29.9973 6H30.1176C30.4285 6.82925 30.7244 7.61603 31.0052 8.36035C31.286 9.10468 31.5543 9.82152 31.81 10.5109C32.0658 11.2003 32.314 11.8672 32.5547 12.5116C32.7954 13.156 33.0361 13.7929 33.2768 14.4223C33.3871 14.7321 33.4899 14.9893 33.5852 15.1941C33.6805 15.399 33.7883 15.6312 33.9086 15.891C33.9287 15.931 33.9738 16.0009 34.044 16.1008C34.1142 16.2007 34.207 16.3031 34.3223 16.408C34.4376 16.5129 34.5806 16.6054 34.7511 16.6853C34.9215 16.7652 35.1221 16.8102 35.3528 16.8202V17H30.9601ZM14.5226 8.72767L14.3722 8.8026C13.961 8.29306 13.5698 7.85846 13.1988 7.49879C12.8277 7.13911 12.4666 6.87436 12.1156 6.70451C11.9451 6.62458 11.6367 6.55715 11.1904 6.5022C10.7441 6.44724 10.2251 6.41977 9.63343 6.41977C9.53314 6.41977 9.4178 6.42227 9.28742 6.42726C9.15705 6.43226 9.03168 6.43725 8.91133 6.44225C8.79098 6.44725 8.67816 6.45224 8.57285 6.45724C8.46755 6.46223 8.38982 6.46473 8.33968 6.46473C8.32965 6.52468 8.31711 6.58462 8.30207 6.64457C8.28702 6.70451 8.27449 6.77445 8.26446 6.85438C8.25443 6.9343 8.2444 7.03671 8.23437 7.1616C8.22434 7.28648 8.21431 7.43884 8.20429 7.61868C8.19426 7.96836 8.18673 8.35051 8.18172 8.76514C8.17671 9.17976 8.1742 9.5719 8.1742 9.94157V11.3653H8.44498C8.60545 11.3653 8.78346 11.3603 8.97903 11.3503C9.1746 11.3403 9.37768 11.3278 9.5883 11.3128C9.79891 11.2978 9.97441 11.2754 10.1148 11.2454C10.4759 11.1655 10.7617 11.0606 10.9723 10.9307C11.1829 10.8008 11.3484 10.6459 11.4687 10.4661C11.5891 10.2863 11.6743 10.0889 11.7245 9.87413C11.7746 9.65932 11.8198 9.42704 11.8599 9.17726H12.0404V14.0029H11.8599C11.7796 13.6832 11.6643 13.346 11.5139 12.9913C11.3634 12.6366 11.1829 12.3644 10.9723 12.1745C10.7818 12.0047 10.5787 11.8773 10.363 11.7924C10.1474 11.7075 9.89418 11.655 9.60334 11.635C9.4529 11.625 9.32253 11.6175 9.21221 11.6125C9.10189 11.6076 8.99658 11.6051 8.89629 11.6051H8.1742V13.4634C8.1742 13.7431 8.17671 14.0004 8.18172 14.2352C8.18673 14.47 8.18924 14.6723 8.18924 14.8421C8.19927 15.1618 8.21682 15.464 8.24189 15.7488C8.26697 16.0335 8.32463 16.2458 8.4149 16.3857C8.50516 16.5256 8.6581 16.633 8.87372 16.7079C9.08935 16.7828 9.40276 16.8303 9.81395 16.8503V17.0002H5V16.8503C5.25073 16.8103 5.4839 16.7454 5.69953 16.6555C5.91515 16.5655 6.09317 16.4407 6.23357 16.2808C6.28372 16.2209 6.32384 16.1334 6.35392 16.0185C6.38401 15.9036 6.41159 15.7713 6.43666 15.6214C6.46174 15.4715 6.47929 15.3142 6.48932 15.1493C6.49934 14.9845 6.50436 14.8221 6.50436 14.6623C6.50436 14.5224 6.50687 14.3326 6.51188 14.0928C6.5169 13.853 6.52191 13.5908 6.52692 13.306C6.53194 13.0213 6.53445 12.729 6.53445 12.4293V8.63775C6.53445 8.45792 6.53194 8.27309 6.52692 8.08326C6.52191 7.89343 6.51188 7.71359 6.49684 7.54375C6.48179 7.3739 6.45672 7.22154 6.42162 7.08666C6.38652 6.95179 6.33888 6.85438 6.27871 6.79443C6.12827 6.63457 5.96781 6.53217 5.79731 6.48721C5.62682 6.44225 5.40618 6.39979 5.13539 6.35982V6.20996H13.4996L14.5226 8.72767Z"
        fill={color}
      />
    </svg>
  );
}

/**
 * Board Collage Icon
 * Mirrors the 3-tile board preview collage: one large tile on the left
 * spanning full height, two smaller stacked tiles on the right.
 * Used for: "Boards" / "Saved ideas" affordances.
 */
export function BoardCollageIcon({
  size = 15,
  className = "",
  color = "currentColor",
}: IconProps) {
  // Aspect ratio matches source drawing (22:15) — `size` controls height.
  const height = size;
  const width = Math.round((size * 22) / 15);
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 22 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <rect
        x="0.5"
        y="0.5"
        width="21"
        height="14"
        rx="1.5"
        fill="white"
        stroke={color}
      />
      <line x1="14.5" y1="0" x2="14.5" y2="15" stroke={color} />
      <line x1="14" y1="7.5" x2="22" y2="7.5" stroke={color} />
    </svg>
  );
}

// Export all icons as a namespace for convenient access
export const SlateIcons = {
  ChevronLeft: ChevronLeftIcon,
  ChevronRight: ChevronRightIcon,
  ChevronDown: ChevronDownIcon,
  ChevronUp: ChevronUpIcon,
  Close: CloseIcon,
  Lock: LockIcon,
  Check: CheckIcon,
  CheckCircleSolid: CheckCircleSolidIcon,
  Search: SearchIcon,
  Info: InfoIcon,
  Warning: WarningIcon,
  ShieldCheck: ShieldCheckIcon,
  Calendar: CalendarIcon,
  PaymentPlan: PaymentPlanIcon,
  FaireLogo: FaireLogo,
  BoardCollage: BoardCollageIcon,
};
